import React, { createContext, useState, useEffect } from 'react';
import { url as API_BASE_URL } from '../MongoDb/MongoConstants';

export const AuthContext = createContext();

// API base URL - Flask backend
// const API_BASE_URL = 'http://localhost:5000';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkUser = async () => {
      try {
        // Check localStorage for stored user ID
        const storedUserId = localStorage.getItem('userId');
        
        if (storedUserId) {
          // Fetch user profile from backend
          await fetchUserProfile(storedUserId);
          setUser({ id: storedUserId });
        } else {
          setUser(null);
          setUserProfile(null);
        }
      } catch (error) {
        console.log("Session check failed:", error);
        // Clear invalid session
        localStorage.removeItem('userId');
        localStorage.removeItem('userEmail');
        setUser(null);
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  // Fetch user profile from MongoDB backend
  const fetchUserProfile = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/${userId}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('User profile not found');
        }
        throw new Error('Failed to fetch user profile');
      }
      const profile = await response.json();
      setUserProfile(profile);
      setUser({ id: userId, email: profile.email });
    } catch (error) {
      console.error("Error fetching user profile:", error);
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      throw error;
    }
  };

  // Register new user
  const register = async (email, password, name) => {
    try {
      console.log("Registering with:", { email, name });
      // Create user in MongoDB via backend
      const response = await fetch(`${API_BASE_URL}/api/user/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          password,
          exchange_avail: 0,
          exchange_nav: 0,
          fund_avail: 0,
          fund_nav: 0,
          sports: [],
          notification_email: true,
          dark_mode: false
        })
      });

      console.log("Registration response status:", response.status);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registration failed');
      }

      const result = await response.json();
      console.log("Registration successful:", result);
      
      // Auto login after registration
      await login(email, password);
      
      return result;
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      console.log("Logging in with:", { email });
      // Validate credentials against backend
      const response = await fetch(`${API_BASE_URL}/api/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      console.log("Login response status:", response.status);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const result = await response.json();
      const userId = result.user_id;

      // Store user ID in localStorage
      localStorage.setItem('userId', userId);
      localStorage.setItem('userEmail', email);

      // Fetch full user profile
      await fetchUserProfile(userId);

      return result;
    } catch (error) {
      console.error("Login error:", error);
      setUser(null);
      setUserProfile(null);
      throw error;
    }
  };

  // Logout user
  const logout = async () => {
    try {
      // Clear localStorage
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      
      // Clear state
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error("Logout error:", error);
      // Even if logout fails, clear the user state
      setUser(null);
      setUserProfile(null);
    }
  };

  // Update user profile
  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');

      const response = await fetch(`${API_BASE_URL}/api/user/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update profile');

      await fetchUserProfile(user.id);
      return true;
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  };

  // Update balance (exchange/fund)
  const updateBalance = async (balanceData) => {
    try {
      if (!user) throw new Error('No user logged in');

      const response = await fetch(`${API_BASE_URL}/api/user/${user.id}/balance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(balanceData)
      });

      if (!response.ok) throw new Error('Failed to update balance');

      await fetchUserProfile(user.id);
      return true;
    } catch (error) {
      console.error("Error updating balance:", error);
      throw error;
    }
  };

  // Add portfolio holding
  const addPortfolioItem = async (securityData) => {
    try {
      if (!user) throw new Error('No user logged in');

      const response = await fetch(`${API_BASE_URL}/api/user/${user.id}/portfolio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(securityData)
      });

      if (!response.ok) throw new Error('Failed to add portfolio item');

      await fetchUserProfile(user.id);
      return true;
    } catch (error) {
      console.error("Error adding portfolio item:", error);
      throw error;
    }
  };

  // Save a bet
  const saveBet = async (betId) => {
    try {
      if (!user) throw new Error('No user logged in');

      const response = await fetch(`${API_BASE_URL}/api/user/${user.id}/save-bet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bet_id: betId })
      });

      if (!response.ok) throw new Error('Failed to save bet');

      await fetchUserProfile(user.id);
      return true;
    } catch (error) {
      console.error("Error saving bet:", error);
      throw error;
    }
  };

  // Update preferences
  const updatePreferences = async (preferences) => {
    try {
      if (!user) throw new Error('No user logged in');

      const response = await fetch(`${API_BASE_URL}/api/user/${user.id}/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences })
      });

      if (!response.ok) throw new Error('Failed to update preferences');

      await fetchUserProfile(user.id);
      return true;
    } catch (error) {
      console.error("Error updating preferences:", error);
      throw error;
    }
  };

  // Update betting stats
  const updateBettingStats = async (stats) => {
    try {
      if (!user) throw new Error('No user logged in');

      const response = await fetch(`${API_BASE_URL}/api/user/${user.id}/betting-stats`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ betting_stats: stats })
      });

      if (!response.ok) throw new Error('Failed to update betting stats');

      await fetchUserProfile(user.id);
      return true;
    } catch (error) {
      console.error("Error updating betting stats:", error);
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    register,
    login,
    logout,
    updateProfile,
    updateBalance,
    addPortfolioItem,
    saveBet,
    updatePreferences,
    updateBettingStats,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};